import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { getDomainUrl, getToken, getUserName, getUserRole, isEmpty, setDomainUrl, setLocalStorage } from "../../utils/helpers";
import { navigateToAuthentication } from "../../utils/authFlow";
import { fetchTenent, getAuthMethod, getTenantDetails } from "../../service/tenantService";
import { Spin } from "antd";
import { useSearchParams } from "react-router-dom";
import { logoutHandler, registerNavigate } from "../../utils/navigateHandler";
import { authorize } from "../../common/SecureRoute";
import { getUserDetails } from "../../service/appService";
import { finishAppSignIn, pendingAppSignIn } from "./authenticatorSignIn";
import { AppHandBack } from "./AuthenticatorSignInPage";

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const domainUrl = getDomainUrl()
  const path = location.pathname;
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  // localStorage token
  const authToken = getToken();
  // session token
  const sessionToken = searchParams.get("session") ?? "";
  const first_login = searchParams.get("first_login") ?? "";

  // APP loader
  const [isAppLoading, setAppLoading] = useState(false);
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  // Set while finishing a sign-in that the Authenticator app started
  // (/authenticator/signin): the token goes to the app, not into the console.
  const [appSignIn, setAppSignIn] = useState(() => (pendingAppSignIn() ? { result: null } : null));
  // A code is single-use: never mint twice (StrictMode runs effects twice).
  const appSignInStarted = useRef(false);

  // Initial Registeration
  useEffect(() => {
    // Register navigate when app mounts
    registerNavigate(navigate);
  }, [navigate]);

  const handleAuthMethodSuccess = useCallback((response) => {
    const authMethod = response?.authenticationmethod?.toUpperCase();
    const ssoUrl = response?.ssoUrl;
    if (authMethod) {
      setLocalStorage("logMethod", authMethod);
      switch (authMethod) {
        case "NORMAL":
          navigateToAuthentication();
          break;
        case "ONELOGIN":
        case "OKTA":
        case "SAML":
          if (ssoUrl) {
            window.location.replace(ssoUrl);
          } else {
            navigateToAuthentication();
          }
          break;
        default:
          navigateToAuthentication();
          break;
      }
    } else {
      navigateToAuthentication();
    }
  }, []);


    const handleTenantSuccess = useCallback(
    (data) => {
      if (data.length < 1) navigate("/tenant/add");
      else navigate("/org/dashboard");
    },
    [navigate]
  );

  const handleTenantFailure = useCallback(() => { }, []);

  const handleRolePath = useCallback((userRole) => {
    if (userRole === "ADMIN") {
      setLocalStorage("layoutView", "admin");
      navigate({ pathname: "/", search: "" }, { replace: true }); 
    }
    else if (userRole === "ENDUSER") {
      setLocalStorage("layoutView", "enduser");
      navigate({ pathname: "/tenant/users/dashboard", search: "" }, { replace: true });
    }
    else if (userRole === "SUPERADMIN") fetchTenent({ payload: { email: getUserName() }, onSuccess: handleTenantSuccess, onFailure: handleTenantFailure });
    else navigate({ pathname: "/", search: "" }, { replace: true });
  }, [navigate, handleTenantSuccess, handleTenantFailure]);

  const handleAuthMethodFailure = useCallback((response) => {
    setAppLoading(false);
    navigateToAuthentication();
  }, []);

  const fetchAuthMethod = useCallback(() => {
    setAppLoading(true);
    getAuthMethod({
      onSuccess: handleAuthMethodSuccess,
      onFailure: handleAuthMethodFailure,
    });
  }, [handleAuthMethodSuccess, handleAuthMethodFailure]);

  const handleTenantDetailsSuccess = useCallback((data) => {
    let tenant = data?.data;
    setLocalStorage("tenantDetails", JSON.stringify(tenant))
    setLocalStorage("Tenantname", tenant?.tenantname);
    setLocalStorage("OrgName", tenant?.organizationName);
    setLocalStorage("timezone", tenant?.timezone || "America/Los_Angeles");
    setTimeout(() => {
      const userRole = getUserRole();
      handleRolePath(userRole);
      setAppLoading(false);
    });
  }, [handleRolePath]);

  const handleTenantDetailsFailure = useCallback((response) => {
    console.error("Tenant Details Failure:", response?.status);
    setAppLoading(false);
  }, []);

  const handleUserDetailsSuccess = useCallback(
    (data) => {
      if (data?.Validation === true && data?.Status === "Success") {
        setLocalStorage("userDetails", JSON.stringify(data));
        setLocalStorage("UserRole", data?.UserRole);
        setLocalStorage("UserName", data?.Username);
        setLocalStorage("userId", data?.UserID);
        setLocalStorage("DomainId", data?.DomainID);
        setLocalStorage("OrgId", data?.OrgID);
        let payload = { email: data?.Username, tenantId: data?.DomainID, orgId: data?.OrgID };
        setTimeout(() => {
          getTenantDetails({ payload, onSuccess: handleTenantDetailsSuccess, onFailure: handleTenantDetailsFailure });
          if (!authorize(data?.UserRole, window.location.pathname)) {
            handleRolePath(data?.UserRole)
          }
        }, 200);
      }
    },
    [handleTenantDetailsSuccess, handleTenantDetailsFailure, handleRolePath]
  );

  const handleUserDetailsFailure = useCallback((response) => {
    logoutHandler();
    setAppLoading(false);
    if (response?.status === 401 && response?.data?.Validation === false && response?.data?.Message === "User Does not exist contact your administrator") {
      setIsUnauthorized(true);
    } else {
      logoutHandler();
    }
  }, []);

  const requiredAccess = useCallback(() => {
    getUserDetails({ param: {}, onSuccess: handleUserDetailsSuccess, onFailure: handleUserDetailsFailure });
  }, [handleUserDetailsSuccess, handleUserDetailsFailure]);

  const handlePath = useCallback(() => {
    if (domainUrl === "app.authnull.com") {
      navigate("/org/login");
    }
    else {
      fetchAuthMethod();
    }
  }, [navigate, fetchAuthMethod, domainUrl]);

  useEffect(() => {
    // The Authenticator app asked for this sign-in. Whichever way it ended —
    // password + MFA, SSO, or a console session already open in this browser —
    // trade the session for a one-time code and hand back to the app.
    const pendingApp = pendingAppSignIn();
    const appToken = token || sessionToken || authToken;
    if (appSignInStarted.current) return;
    if (pendingApp && !isEmpty(appToken)) {
      appSignInStarted.current = true;
      if (token || sessionToken) {
        setLocalStorage("token", appToken);
        setSearchParams({}, { replace: true });
      }
      finishAppSignIn(pendingApp).then((result) => setAppSignIn({ result }));
      return;
    }

    if (!isEmpty(token) && !isEmpty(first_login)) {
      // Authentication token flow
      setLocalStorage("token", token);
      setAppLoading(true);
      setSearchParams({}, { replace: true });
      requiredAccess();
    } else if (!isEmpty(authToken)) {
      // localStorage token flow
      requiredAccess();
    } else if (!isEmpty(sessionToken)) {
      // session token flow
      setLocalStorage("token", sessionToken);
      setAppLoading(true);
      setSearchParams({}, { replace: true });
      requiredAccess();
    } else {
      handlePath();
    }
  }, [token, first_login, authToken, sessionToken, path, handlePath, requiredAccess, setSearchParams]);

  useEffect(() => {
    setDomainUrl();
  }, []);

  if (appSignIn?.result || (appSignIn && (token || sessionToken || authToken))) {
    return <AppHandBack result={appSignIn.result} />;
  }

  return (
    <>
      {isAppLoading && (
        <div className="app-container">
          <div className="app-loading">
            <Spin size="large" />
          </div>
        </div>
      )}
    </>
  );
}

export default Login;
